import 'katex/dist/katex.min.css';

import React, { useEffect, useRef } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { TFunction } from '@lyearn/i18n';
import { ArticleOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import { linkStyles } from '@/components/MessageItem/MessageBody/styles';
import { getAdaptedMarkup } from '@/components/MessageItem/MessageBody/utils';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';
import useScrollProgress from '@/hooks/useScrollProgress';
import AIResponseFeedback from '@/modules/corporate/modules/channel/views/ChannelDetails/components/MessageListItem/components/AIResponseFeedback';
import { CreatePostMeta, Maybe } from '@/types/schema';

import { NoteFragment } from '../../graphql/queries/generated/notes';
import { ChatbotMessageType } from '../../types';
import SourceCards from '../SourceCards';
import { highlightCodeBlocks } from './highlightCodeBlocks';
import {
  aiAnswerStyles,
  anchorNone,
  markupContainerStyles,
  markupHelperStyles,
  mentionHelperStyles,
  overflowOverlay,
} from './styles';
import TeachModeFollowUps from './TeachModeFollowUps';

function BotMessage({
  textContent,
  doc,
  id,
  updatedAt, // updatedAt is only provided for getPost-verified messages
  t,
  references,
  channel,
  allowRegeneration,
  setGenerativeTaskId,
  setMessages,
  setIsWaiting,
  setCurrentNote,
  setHideNoteButtons,
  pinnedItems,
  teachModeEnabled,
  onClickFollowUp,
}: ChatbotMessageType & {
  t: TFunction;
  channel?: Maybe<ChatFragment>;
  allowRegeneration: boolean; // last index
  setGenerativeTaskId?: (id: string) => void;
  setMessages: React.Dispatch<React.SetStateAction<ChatbotMessageType[]>>;
  setIsWaiting?: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentNote?: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  setHideNoteButtons?: React.Dispatch<React.SetStateAction<boolean>>;
  pinnedItems: any[];
  teachModeEnabled: boolean;
  onClickFollowUp?: (value: string, meta?: CreatePostMeta) => void;
}) {
  const { isMobile } = useScreen();
  const docRef = useRef<HTMLDivElement>(null);
  const answerRef = useRef<HTMLSpanElement>(null);
  const { scrollProgress, canScroll } = useScrollProgress(docRef.current);

  // Apply Prism syntax highlighting to any code blocks in the answer. Re-runs as
  // the streamed answer grows (React re-sets the innerHTML, clearing the guard).
  useEffect(() => {
    if (answerRef.current) {
      highlightCodeBlocks(answerRef.current);
    }
  }, [textContent]);

  if (!textContent && !doc) {
    return null;
  }

  return (
    <Stack className="-mt-20">
      <Stack className="flex-shrink-0 mt-10 w-[30px] h-[30px]" justifyContent="start"></Stack>
      <Stack
        aria-labelledby={id}
        className={cn(
          anchorNone,
          linkStyles,
          'rounded-8 self-start justify-self-start w-full',
          isMobile ? 'py-12 mr-12 ml-6' : 'p-12 mr-[3.6rem] ml-12 ',
        )}
        direction="col">
        <Typography color="text-text-primary" variant="body-long-02">
          <span
            dangerouslySetInnerHTML={getAdaptedMarkup(t, textContent, null)}
            ref={answerRef}
            className={cn(
              'markup-container',
              markupContainerStyles,
              markupHelperStyles,
              mentionHelperStyles,
              aiAnswerStyles,
            )}
          />
        </Typography>

        {/* knowledge doc */}
        {doc ? (
          <Stack
            className="p-16 pt-12 mt-22 mb-16 border border-border rounded-8 shadow-02 relative"
            direction="col"
            gap={16}>
            <Stack alignItems="center" direction="row" justifyContent="between">
              <Stack alignItems="center" direction="row" gap={8} justifyContent="start">
                <Stack className="flex-shrink-0">
                  <ArticleOutline fillColorToken="icon-secondary" height={24} width={24} />
                </Stack>
                <Typography color="text-text-secondary" variant="body-short-02">
                  {`Knowledge doc`}
                </Typography>
              </Stack>
            </Stack>

            <Stack ref={docRef} className="max-h-[27.4rem] overflow-y-auto">
              <Typography color="text-text-primary" variant="body-long-02">
                <span
                  dangerouslySetInnerHTML={getAdaptedMarkup(t, doc, null)}
                  className={cn('markup-container', markupContainerStyles, markupHelperStyles)}
                />
              </Typography>
            </Stack>
            <Box
              className={cn(overflowOverlay, {
                hidden: scrollProgress >= 100 || !canScroll,
              })}
            />
          </Stack>
        ) : null}

        {updatedAt ? (
          <AIResponseFeedback
            allowRegeneration={allowRegeneration}
            channel={channel}
            messageId={id}
            setCurrentNote={setCurrentNote}
            setGenerativeTaskId={setGenerativeTaskId}
            setIsWaiting={setIsWaiting}
            setMessages={setMessages}
            teachModeEnabled={teachModeEnabled}
            textContent={textContent}
          />
        ) : null}

        <SourceCards
          pinnedItems={pinnedItems}
          references={references}
          setCurrentNote={setCurrentNote}
          setHideNoteButtons={setHideNoteButtons}
        />

        {teachModeEnabled && updatedAt && allowRegeneration ? (
          <TeachModeFollowUps onClickFollowUp={onClickFollowUp!} />
        ) : null}
      </Stack>
    </Stack>
  );
}

export default BotMessage;
